import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { OnboardingFlow } from "@/components/OnboardingFlow";
import { AuthCallback } from "@/components/AuthCallback";
import { AuthGatekeeper } from "@/components/AuthGatekeeper";
import { LegalRedirect } from "@/components/LegalRedirect";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { NavigationProvider, ROUTES } from "@/contexts/NavigationContext";
import { useAppConfig } from "@/hooks/useAppConfig";
import { useEffect } from "react";
import { fetchRemoteConfig, trackEvent } from "@/lib/firebase";

/**
 * FirebaseServicesInit - Initialize Firebase Analytics and Remote Config
 */
function FirebaseServicesInit() {
    useEffect(() => {
        // Initialize Remote Config on app load
        fetchRemoteConfig().then((activated) => {
            console.log('📊 Remote Config activated:', activated);
        });
        
        // Track app open event
        trackEvent('app_open', {
            platform: 'web',
            timestamp: new Date().toISOString()
        });
    }, []);
    
    return null;
}

/**
 * BackButtonHandler - Prevents app from closing on Android back button
 */
function BackButtonHandler() {
    useEffect(() => {
        // Handle Android/Browser back button
        const handleBackButton = (e) => {
            // Check if we're at the root
            if (window.history.length <= 1) {
                // Prevent app close - show nothing or toast
                e.preventDefault();
                return;
            }
        };

        // Listen for beforeunload to catch edge cases
        window.addEventListener('beforeunload', handleBackButton);
        
        // Push initial state to history to prevent immediate close
        if (window.history.length <= 1) {
            window.history.pushState({ page: 'app' }, '', window.location.href);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBackButton);
        };
    }, []);

    return null;
}

/**
 * AppRouter handles routing including OAuth callback detection.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * 
 * Deep Linking Routes:
 * - / or /sanctuary → Dashboard
 * - /tarot → Tarot Reader
 * - /orbit → Orbit Section
 * - /dreams → Dream Sanctuary
 * - /settings → Settings Panel
 * 
 * URL Parameter Deep Links (from push notifications):
 * - ?view=horoscope → Daily Horoscope
 * - ?view=oracle → Oracle Chat
 * - ?view=transits → Transit Report
 * - ?view=moon → Moon Phase
 * - ?view=retrograde → Mercury Retrograde Guide
 * - ?view=birthday → Solar Return
 * 
 * Public Routes (NO AUTH REQUIRED):
 * - /privacy → Privacy Policy
 * - /terms → Terms of Service
 */
function AppRouter() {
    const location = useLocation();
    // Load app config (including Google Maps key) on app start
    const { config, loading: configLoading } = useAppConfig();
    
    // Parse URL parameters for deep linking
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    const actionParam = searchParams.get('action');
    
    // Map URL params to sections
    const getInitialSectionFromParams = () => {
        const deepLinkMap = {
            'horoscope': 'dashboard',
            'oracle': 'oracle',
            'transits': 'dashboard',
            'moon': 'dashboard',
            'retrograde': 'dashboard',
            'birthday': 'dashboard',
            'opportunity': 'dashboard',
            'calendar': 'dashboard',
            'tarot': 'tarot',
            'dreams': 'dreams',
            'settings': 'settings',
            'profile': 'profile',
            'today': 'dashboard'
        };
        
        // Check view param first, then action param
        const param = viewParam || actionParam;
        if (param && deepLinkMap[param]) {
            console.log(`🔗 Deep link detected: ${param} → ${deepLinkMap[param]}`);
            return deepLinkMap[param];
        }
        return null;
    };
    
    const deepLinkSection = getInitialSectionFromParams();
    
    // Check URL fragment for session_id (OAuth callback) - synchronous check
    // This MUST happen during render, NOT in useEffect (prevents race conditions)
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }
    
    // PUBLIC ROUTES - No authentication required (for Google OAuth verification)
    const publicPaths = ['/privacy', '/terms', '/privacy.html', '/terms.html'];
    const isPublicRoute = publicPaths.some(path => location.pathname === path);
    
    if (isPublicRoute) {
        // Render legal pages WITHOUT AuthGatekeeper
        return (
            <Routes>
                <Route path="/privacy" element={<LegalRedirect to="/privacy.html" />} />
                <Route path="/terms" element={<LegalRedirect to="/terms.html" />} />
                <Route path="/privacy.html" element={<LegalRedirect to="/privacy.html" />} />
                <Route path="/terms.html" element={<LegalRedirect to="/terms.html" />} />
            </Routes>
        );
    }
    
    // PROTECTED ROUTES - Wrapped in AuthGatekeeper
    return (
        <AuthGatekeeper>
            <NavigationProvider>
                <BackButtonHandler />
                <div className="App">
                    <Routes>
                        {/* All routes handled by OnboardingFlow which manages internal state */}
                        {/* Pass deep link section from URL params */}
                        <Route path="/" element={<OnboardingFlow initialSection={deepLinkSection} deepLinkView={viewParam || actionParam} />} />
                        <Route path="/sanctuary" element={<OnboardingFlow initialSection="dashboard" />} />
                        <Route path="/tarot" element={<OnboardingFlow initialSection="tarot" />} />
                        <Route path="/orbit" element={<OnboardingFlow initialSection="orbit" />} />
                        <Route path="/dreams" element={<OnboardingFlow initialSection="dreams" />} />
                        <Route path="/settings" element={<OnboardingFlow initialSection="settings" />} />
                        <Route path="/profile" element={<OnboardingFlow initialSection="profile" />} />
                        
                        {/* Catch-all redirect to home */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </div>
            </NavigationProvider>
        </AuthGatekeeper>
    );
}

/**
 * App - Root component
 * 
 * Route-based Authentication:
 * - Public routes (/privacy, /terms) render WITHOUT AuthGatekeeper
 * - Protected routes render WITH AuthGatekeeper
 * 
 * The AuthGatekeeper ensures:
 * 1. Full-screen loading animation shown immediately
 * 2. Firebase auth check happens BEFORE any content renders
 * 3. User never sees onboarding if already logged in
 * 
 * NOTE: Legal pages are PUBLIC for Google OAuth verification compliance.
 */
function App() {
    return (
        <BrowserRouter>
            <LanguageProvider>
                <FirebaseServicesInit />
                <AppRouter />
            </LanguageProvider>
        </BrowserRouter>
    );
}

export default App;
